/* eslint-disable camelcase */
import { RefinementCtx, z } from 'zod'

import { UserIntention } from '@/constants/user';
import { URL_REGEX } from '@/lib/constants'

const confirmPasswordValidation = ({ password, confirmPassword }: { password: string; confirmPassword: string }) => {
  return password === confirmPassword
}

const EMAIL_MAX_LENGTH = 128
const PASSWORD_MIN_LENGTH = 8
const PASSWORD_MAX_LENGTH = 100

export const LoginSchema = z.object({
  email: z.string().trim().min(1).max(EMAIL_MAX_LENGTH).email(),
  password: z.string().trim().superRefine(_superRefinePassword),
})

export const LoginSocialSchema = z.object({
  provider: z.enum(['google']),
  callbackURL: z.string().trim().min(1).url().optional(),
  newUserCallbackURL: z.string().trim().min(1).url().optional(),
  requestSignUp: z.boolean().optional(),
  errorCallbackURL: z.string().trim().min(1).url().optional(),
})

export const SignUpSchema = z
  .object({
    name: z.string().trim().min(1).max(30),
    email: z.string().trim().min(1).max(EMAIL_MAX_LENGTH).email(),
    password: z.string().trim().superRefine(_superRefineChangePassword),
    confirmPassword: z.string().trim().superRefine(_superRefineChangePassword),
    timezone: z.string().optional(),
    appSumoCode: z.string().optional(),
    utm_source: z.string().optional(),
    utm_medium: z.string().optional(),
    utm_campaign: z.string().optional(),
    intention: z.nativeEnum(UserIntention).optional(),
  })
  .refine(
    ({ password, confirmPassword }) => {
      return confirmPasswordValidation({ password, confirmPassword })
    },
    {
      params: {
        i18n: {
          key: 'custom.password_do_not_match',
        },
      },
      path: ['confirmPassword'],
    },
  )

export const SignUpUpdateUserSchema = z.object({
  name: z.string().trim().min(1).max(30),
})

export const SignUpConfirmSchema = z.object({
  email: z.string().trim().min(1).email(),
  confirmationCode: z.string().trim().min(1),
})

export const SignUpResendVerificationEmailSchema = z.object({
  email: z.string().trim().min(1).email(),
})

export const SignUpResendCodeSchema = z.object({
  email: z.string().trim().min(1).email(),
})

export const ForgotPasswordSchema = z.object({
  email: z.string().trim().min(1).max(EMAIL_MAX_LENGTH).email(),
})

export const ResetPasswordSchema = z
  .object({
    password: z.string().trim().superRefine(_superRefineChangePassword),
    confirmPassword: z.string().trim().superRefine(_superRefineChangePassword),
  })
  .refine(
    ({ password, confirmPassword }) => {
      return confirmPasswordValidation({ password, confirmPassword })
    },
    {
      params: {
        i18n: {
          key: 'custom.password_do_not_match',
        },
      },
      path: ['confirmPassword'],
    },
  )

export const ChangePasswordSchema = z
  .object({
    oldPassword: z.string().trim().min(1),
    newPassword: z.string().trim().superRefine(_superRefineChangePassword),
    confirmNewPassword: z.string().trim().superRefine(_superRefineChangePassword),
    revokeOtherSessions: z.boolean().optional(),
  })
  .refine(
    ({ newPassword, confirmNewPassword }) => {
      return confirmPasswordValidation({ password: newPassword, confirmPassword: confirmNewPassword })
    },
    {
      params: {
        i18n: {
          key: 'custom.password_do_not_match',
        },
      },
      path: ['confirmNewPassword'],
    },
  )

export const CogintoLanguageSchema = z.object({
  language: z.string().min(1),
})
export const TimeZoneSchema = z.object({
  timezone: z.string().min(1),
})

export const OnboardingSchema = z.object({
  rootUrl: z
    .string()
    .trim()
    .min(1)
    .max(250)
    .refine(
      (value) => {
        return URL_REGEX.test(value)
      },
      {
        params: {
          i18n: {
            key: 'custom.url.invalid_verbose_cannot_connect',
          },
        },
      },
    ),
})

function _superRefinePassword(value: string, ctx: RefinementCtx) {
  if (value.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      type: 'string',
      inclusive: true,
      minimum: 1,
    })
  } else if (value.length < PASSWORD_MIN_LENGTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      type: 'string',
      inclusive: true,
      minimum: PASSWORD_MIN_LENGTH,
    })
  } else if (value.length > PASSWORD_MAX_LENGTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      type: 'string',
      inclusive: true,
      maximum: PASSWORD_MAX_LENGTH,
    })
  }
}

function _superRefineChangePassword(value: string, ctx: RefinementCtx) {
  if (value.length < PASSWORD_MIN_LENGTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      type: 'string',
      inclusive: true,
      minimum: PASSWORD_MIN_LENGTH,
    })
  } else if (value.length > PASSWORD_MAX_LENGTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      type: 'string',
      inclusive: true,
      maximum: PASSWORD_MAX_LENGTH,
    })
  }
}
